import {TFunction} from 'i18next';
import {capitalize, filter, find, forEach, join, map, replace, snakeCase} from 'lodash';
import moment, {DurationInputArg2} from 'moment';

import {DATE_FORMAT_SS} from 'constants/format';
import {DateFormat} from 'enums';
import {AccountAssignmentInfo, ClientParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {BaseUserRole, User, UserRole, ClientConnect, UserSummary, CreditCard, UserParticipantRole} from 'types';
import {userAuthUtils} from 'utils';
import {sessionUtils} from 'utils/session';

const hour = 3600; // 1 hour in seconds

// TODO: other currencies
const CURRENCY_SYMBOL_MAP = {
    USD: '$'
};

const formatPrice = (value: number, currency: string): string => {
    if (CURRENCY_SYMBOL_MAP[currency]) {
        return `${CURRENCY_SYMBOL_MAP[currency]}${value}`;
    } else {
        return `${value} ${currency}`;
    }
};

const formatCreditCardLabel = (card: CreditCard, t: TFunction): string =>
    card ? `${capitalize(card.brand)} ${t(TRANSLATION_KEYS.USER.ACCOUNT.BILLING.PAYMENT_SOURCE.CARD.ENDING)} ${card.last4}` : null;

const formatCreditCardExpiry = (card: CreditCard): string =>
    card ? `${card.expiryMonth}/${card.expiryYear}` : null;

export const tryParseJson = <T>(stringifiedJson: string): {success: boolean, value?: T} => {
    try {
        return {success: true, value: JSON.parse(stringifiedJson)};
    } catch {
        return {success: false};
    }
};

export const parseJsonOrObject = <T>(stringifiedJson: string | T): {success: boolean, value?: T} => {
    if (typeof stringifiedJson === 'object') {
        return {success: true, value: stringifiedJson};
    } else if (typeof stringifiedJson === 'string') {
        return tryParseJson(stringifiedJson);
    }

    return {success: false};
};

export const formatDate = (date: Date | string, timeZone: string, format: string = DATE_FORMAT_SS) =>
    date ? moment.tz(date, timeZone).format(format) : null;

// TODO: use right imports in components directly from session utils
export const getParticipantName = (data: {firstName?: string, lastName?: string}, defaultName = '') =>
    sessionUtils.getParticipantName(data, defaultName);
export const getUserName = (t: TFunction, data: {firstName?: string, lastName?: string}, isMe = false) =>
    sessionUtils.getParticipantNameWithMe(t, data, isMe);

export const findUserName = (t: TFunction, users: UserSummary[], conditions: {eid?: number, email?: string}) => {
    if (!conditions?.eid && !conditions?.email) {
        return null;
    }
    const user = find(users, conditions) as User;
    const fullName = getParticipantName(user);
    const localUser = userAuthUtils.getLocalUser();
    const isMe = conditions?.eid === localUser.eid || conditions?.email === localUser.email;
    return `${fullName}` + (isMe ? ` (${t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.ME)})` : '');
};

export const formatDuration = (value: number, unit: DurationInputArg2, format: string) => {
    const start = moment();
    const end = moment(start).add(value, unit);
    const diff = end.diff(start);

    return moment.utc(diff).format(format);
};

export const formatSeconds = (value: number, format = 'mm:ss') => {
    if (hour <= value) {
        format = 'HH:mm:ss';
    }
    return formatDuration(value, 'seconds', format);
};

export const getPercentage = (value: number, total: number) => {
    if (!total) {
        return 0;
    }
    const result = (value / total * 100);
    if (result === 100) {
        return 100;
    }
    return +result.toFixed(2);
};

export const buildTranslationKey = (name: string) => snakeCase(name).toUpperCase();

export const getRoleName = (t: TFunction, roleId: string, availableRoles: UserRole[]) => {
    if (roleId === BaseUserRole.AppAdmin) {
        return;
    } else if (roleId === BaseUserRole.Admin) {
        return t(TRANSLATION_KEYS.COMMON.USER.ROLE.ADMIN) + '*';
    } else if (roleId === BaseUserRole.Agent) {
        return t(TRANSLATION_KEYS.COMMON.USER.ROLE.AGENT) + '*';
    } else {
        return find(availableRoles, {id: roleId})?.name;
    }
};

export const getInSessionRoleName = (t: TFunction, inSessionUserRole: UserParticipantRole) =>
    t(TRANSLATION_KEYS.COMMON.USER.IN_SESSION_USER_ROLE[buildTranslationKey(inSessionUserRole)]);

export const getInSessionRoleNames = (t: TFunction, inSessionUserRoles: UserParticipantRole[]) =>
    join(filter(map(inSessionUserRoles, (inSessionUserRole) => getInSessionRoleName(t, inSessionUserRole))), ', ');

// TODO: Sort roles to have default roles first
export const getRolesName = (t: TFunction, roleIds: string[], availableRoles: UserRole[]) =>
    filter(map(roleIds, (roleId) => getRoleName(t, roleId, availableRoles)));

export const getRolesNames = (t: TFunction, roleIds: string[], availableRoles: UserRole[]) =>
    join(getRolesName(t, roleIds, availableRoles), ', ');

export const removeBase64Prefix = (base64: string) => base64?.replace(/^data:image\/[a-z]+;base64,/, '');

export const fromParticipantToClientConnect = (
    client: ClientParticipant,
    data: {agentEmail: string, assignment: AccountAssignmentInfo}
): Partial<ClientConnect> => {
    const userInfo = client?.data?.userInfo;
    return {
        userInfo,
        assignmentInfo: data?.assignment,
        ...(data?.agentEmail && {
            agent: {
                email: data.agentEmail
            }
        })
    };
};

const substituteLink = (path: string, substitutions: object) => {
    forEach(substitutions, (value, key) => {
        path = replace(path, `:${key}`, value);
    });
    return path;
};

const getFileDateFormat = (dateFormat: DateFormat) => dateFormat.replace(/\//g, '_');

const formatBoolean = (t: TFunction, value: boolean) => value ? t(TRANSLATION_KEYS.COMMON.ALL.YES) : t(TRANSLATION_KEYS.COMMON.ALL.NO);

export const transformUtils = {
    removeBase64Prefix,
    fromParticipantToClientConnect,
    formatPrice,
    getInSessionRoleName,
    getInSessionRoleNames,
    formatCreditCardLabel,
    formatCreditCardExpiry,
    substituteLink,
    getFileDateFormat,
    formatBoolean
};
